import React from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CheckInHistory = ({ checkIns, loading, onRefresh }) => {
  const formatDuration = (minutes) => {
    if (!minutes) return 'In progress';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'qr_code':
        return 'QrCode';
      case 'manual':
        return 'User';
      case 'biometric':
        return 'Fingerprint';
      case 'card_scan':
        return 'CreditCard';
      default:
        return 'LogIn';
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Check-In History</h2>
          <p className="text-sm text-muted-foreground">Your recent gym visits</p>
        </div>
        <Button onClick={onRefresh} variant="outline" disabled={loading}>
          <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </Button>
      </div>

      {loading && checkIns?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : checkIns?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Check-Ins Yet</h3>
          <p className="text-muted-foreground">Your check-in history will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {checkIns?.map((checkIn) => (
            <div
              key={checkIn?.id}
              className="p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Icon name={getMethodIcon(checkIn?.check_in_method)} size={20} className="text-primary" />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">
                        {format(new Date(checkIn?.check_in_time), 'EEEE, MMM d, yyyy')}
                      </h3>
                      {!checkIn?.check_out_time && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-success/10 text-success rounded-full">
                          Active
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Icon name="Clock" size={14} />
                        <span>Check-in: {format(new Date(checkIn?.check_in_time), 'h:mm a')}</span>
                      </div>
                      {checkIn?.check_out_time && (
                        <div className="flex items-center gap-1.5">
                          <Icon name="Clock" size={14} />
                          <span>Check-out: {format(new Date(checkIn?.check_out_time), 'h:mm a')}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Icon name="MapPin" size={14} />
                        <span>{checkIn?.location || 'Main Gym'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="Timer" size={14} />
                        <span>Duration: {formatDuration(checkIn?.duration_minutes)}</span>
                      </div>
                    </div>

                    {checkIn?.notes && (
                      <p className="mt-2 text-sm text-muted-foreground italic">{checkIn?.notes}</p>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">
                    {formatDuration(checkIn?.duration_minutes)}
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {checkIn?.check_in_method?.replace('_', ' ')}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {checkIns?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">{checkIns?.length}</div>
              <div className="text-xs text-muted-foreground">Total Visits</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                {Math.round(
                  checkIns?.reduce((sum, c) => sum + (c?.duration_minutes || 0), 0) / checkIns?.length
                )}
              </div>
              <div className="text-xs text-muted-foreground">Avg Minutes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground mb-1">
                {checkIns?.filter((c) => !c?.check_out_time)?.length}
              </div>
              <div className="text-xs text-muted-foreground">Active Now</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInHistory;